import type { GanttFeature } from '@/components/kibo-ui/gantt'
import {
  GanttFeatureList,
  GanttFeatureRow,
  GanttHeader,
  GanttProvider,
  GanttSidebar,
  GanttSidebarItem,
  GanttTimeline,
} from '@/components/kibo-ui/gantt'
import { Card } from '@/components/kibo-ui/gantt/card'
import { DriverActivityType } from '@/modules/activities/consts'
import { format } from '@/utils/date-fns'
import { useActivityColor } from '@/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  addHours,
  addMinutes,
  differenceInMinutes,
  isSameDay,
  max,
  min,
  parseISO,
  startOfHour,
  subHours,
} from 'date-fns'
import { Minus, Plus } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import type { InfringementQueryInfringement } from '../types.graphl'

// Activity bar colors are derived per-activity via `useActivityColor` so we
// stay in sync with the salary-gantt palette. Non-activity palette values
// (danger/ink/border) stay local because they're UI-chrome rather than
// activity-type semantics.
const COLORS = {
  danger: '#DC2626',
  ink: '#0F1F3A',
  muted: '#5C6B85',
  faint: '#94A3B8',
  border: '#E2E8F0',
  dangerBg: '#FEF2F2',
  dangerInk: '#7F1D1D',
}

const GANTT_COLUMN_WIDTH_PX = 5000
const FERRY_EVENT_WIDTH_MINUTES = 5

// Discrete zoom stops (percent). Non-uniform on the zoom-out side so wide
// windows (multi-day infringements) get fine-grained control without
// dragging through 50 clicks. Keep 100/200/400 as exact stops — minorStepMin
// switches at those thresholds.
const ZOOM_LEVELS = [10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 75, 100, 200, 400]
const ZOOM_MIN = ZOOM_LEVELS[0]!
const ZOOM_MAX = ZOOM_LEVELS[ZOOM_LEVELS.length - 1]!

function prevZoom(z: number): number {
  for (let i = ZOOM_LEVELS.length - 1; i >= 0; i--) {
    if (ZOOM_LEVELS[i]! < z) return ZOOM_LEVELS[i]!
  }
  return ZOOM_MIN
}

function nextZoom(z: number): number {
  for (const lvl of ZOOM_LEVELS) {
    if (lvl > z) return lvl
  }
  return ZOOM_MAX
}

type RowKey =
  | 'drive'
  | 'work'
  | 'availability'
  | 'pause'
  | 'rest'
  | 'shortBreak'
  | 'unavailable'
  | 'ferry'

type FerrySpecificCondition = {
  id: string
  timestamp: string
  type: 'FERRY' | 'FERRY_START' | 'FERRY_END'
}

type RestKind = 'weeklyRest' | 'dailyRest' | 'shortBreak'

// Raw tachograph data has only one rest type. Daily vs weekly classification
// is derived from the total duration of a contiguous rest block — see
// `buildRestKindByActivityId` below. Thresholds follow EU 561/2006.
const WEEKLY_REST_MIN_MINUTES = 24 * 60
const DAILY_REST_MIN_MINUTES = 9 * 60

function isRestActivity(type: string): boolean {
  return type === DriverActivityType.REST
}

function classifyRestKind(totalMinutes: number): RestKind {
  if (totalMinutes >= WEEKLY_REST_MIN_MINUTES) return 'weeklyRest'
  if (totalMinutes >= DAILY_REST_MIN_MINUTES) return 'dailyRest'
  return 'shortBreak'
}

// Group contiguous REST activities (gap < 1 min) and tag every member with
// the rest-kind its total duration classifies into. Tachograph fragments rest at
// midnight / vehicle changes; the regulatory clock keeps running, so a
// fragmented 11h block is still a regular daily rest.
function buildRestKindByActivityId(
  activities: ReadonlyArray<{
    id: string
    activityType: string
    datetimeStart: string
    datetimeEnd: string
  }>
): Map<string, RestKind> {
  const rests = activities
    .filter(a => isRestActivity(a.activityType))
    .map(a => ({
      id: a.id,
      start: parseISO(a.datetimeStart),
      end: parseISO(a.datetimeEnd),
    }))
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  const result = new Map<string, RestKind>()
  let groupIds: string[] = []
  let groupStart: Date | null = null
  let groupEnd: Date | null = null

  const flush = () => {
    if (groupStart && groupEnd && groupIds.length > 0) {
      const kind = classifyRestKind(differenceInMinutes(groupEnd, groupStart))
      groupIds.forEach(id => result.set(id, kind))
    }
    groupIds = []
    groupStart = null
    groupEnd = null
  }
  for (const r of rests) {
    if (groupEnd && differenceInMinutes(r.start, groupEnd) > 1) flush()
    if (!groupStart) groupStart = r.start
    groupEnd = r.end
    groupIds.push(r.id)
  }

  flush()
  return result
}

function activityTypeToRow(type: string): RowKey | null {
  switch (type) {
    case DriverActivityType.DRIVE:
      return 'drive'
    case DriverActivityType.WORK:
      return 'work'
    case DriverActivityType.AVAILABLE:
      return 'availability'
    case DriverActivityType.NOT_AVAILABLE:
      return 'unavailable'
    case DriverActivityType.PAID_REST:
      return 'pause'
    default:
      return null
  }
}

function categoryLetter(identifier: string | null | undefined): string {
  return (identifier ?? '').charAt(0).toUpperCase()
}

function overlayRowForCategory(identifier: string | null | undefined): RowKey {
  switch (categoryLetter(identifier)) {
    case 'D':
      // Nominal sentinel — Cat D's overlay is the deficit band painted past
      // periodEnd in `bandSegments`, not a per-feature overlay, so this row
      // never gets enriched (see `enrichedFeatures` early-return for
      // deficit categories).
      return 'rest'
    case 'B':
    case 'C':
      return 'drive'
    default:
      return 'drive'
  }
}

// Category-family classification. Identifiers follow EU 561 coding:
//   B1–B8    daily driving overage
//   B9–B12   weekly driving overage (56h)
//   B13–B16  fortnightly driving overage (90h / 2 weeks)
//   C1–C3    break infringement (4h30 continuous driving rule)
//   D1–D3    ordinary daily rest deficit (11h minimum)
//   D4–D6    reduced daily rest deficit (9h minimum, max 3x/week)
//   D7–D9    split daily rest deficit (3h + ≥9h)
//   D10–D12  multi-manning daily rest deficit (Art. 8 nr. 5: 9h within
//            a 30h window instead of the normal 24h). Source:
//            Arbeidstilsynet "Kjøre- og hviletidsheftet", Annex III
//            severity table. Different rule, not a variant of split rest.
//   D13–D18  weekly rest deficit (24h / 45h)
//   D19–D21  overdue weekly rest start (reason_value = hours LATE, not
//            rest taken; lower/upper_limit are lateness tiers, not a
//            duration threshold) — routed through D_OVERDUE_REST so the
//            rest-deficit copy and stat cards are skipped.
// Different families require different regulatory copy ("reduced" vs
// "ordinary" etc.) even though the underlying math is identical. Kept as a
// single helper so the title/hint/overlay-row logic can't drift apart.
type CategoryFamily =
  | 'B_DAILY'
  | 'B_WEEKLY'
  | 'B_FORTNIGHTLY'
  | 'C_BREAK'
  | 'D_ORDINARY'
  | 'D_REDUCED'
  | 'D_SPLIT'
  | 'D_MULTI_MANNING'
  | 'D_WEEKLY'
  | 'D_OVERDUE_REST'
  | 'UNKNOWN'

function categoryFamily(identifier: string | null | undefined): CategoryFamily {
  const raw = (identifier ?? '').toUpperCase()
  const letter = raw.charAt(0)
  const n = parseInt(raw.slice(1), 10)
  if (letter === 'B') {
    if (n >= 13) return 'B_FORTNIGHTLY'
    if (n >= 9) return 'B_WEEKLY'
    if (n >= 1) return 'B_DAILY'
  }
  if (letter === 'C') return 'C_BREAK'
  if (letter === 'D') {
    // D19–D21 must be checked before the D_WEEKLY range — they share the
    // "D" letter but are an unrelated offence (overdue weekly rest start),
    // not a rest-duration deficit.
    if (n >= 19) return 'D_OVERDUE_REST'
    if (n >= 13) return 'D_WEEKLY'
    if (n >= 10) return 'D_MULTI_MANNING'
    if (n >= 7) return 'D_SPLIT'
    if (n >= 4) return 'D_REDUCED'
    if (n >= 1) return 'D_ORDINARY'
  }
  return 'UNKNOWN'
}

type StatCardProps = {
  label: string
  value: string
  hint?: string
  tone?: 'default' | 'danger'
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  hint,
  tone = 'default',
}) => {
  const isDanger = tone === 'danger'
  return (
    <div
      className="flex flex-1 flex-col gap-1.5 rounded-xl p-[18px]"
      style={{
        background: isDanger ? COLORS.dangerBg : '#FFFFFF',
        border: `${isDanger ? 1.5 : 1}px solid ${
          isDanger ? COLORS.danger : COLORS.border
        }`,
      }}
    >
      <span
        className="text-[11px] font-semibold"
        style={{ color: isDanger ? COLORS.dangerInk : COLORS.muted }}
      >
        {label}
      </span>
      <span
        className="text-[22px] font-bold leading-tight md:text-[28px]"
        style={{ color: isDanger ? COLORS.danger : COLORS.ink }}
      >
        {value}
      </span>
      {hint && (
        <span
          className="text-[11px]"
          style={{
            color: isDanger ? COLORS.dangerInk : COLORS.faint,
            fontWeight: isDanger ? 600 : 400,
          }}
        >
          {hint}
        </span>
      )}
    </div>
  )
}

const StoryCard: React.FC<{
  step: number
  time: string
  title: string
  body: string
  tone?: 'default' | 'danger'
}> = ({ step, time, title, body, tone = 'default' }) => {
  const danger = tone === 'danger'
  return (
    <div
      className="flex flex-1 flex-col gap-2.5 rounded-xl p-[18px]"
      style={{
        background: danger ? COLORS.dangerBg : '#FFFFFF',
        border: `${danger ? 1.5 : 1}px solid ${
          danger ? COLORS.danger : COLORS.border
        }`,
      }}
    >
      <div className="flex items-center justify-between">
        <div
          className="flex h-6 w-6 items-center justify-center rounded-full"
          style={{ background: danger ? COLORS.danger : COLORS.muted }}
        >
          <span className="text-[12px] font-bold text-white">{step}</span>
        </div>
        <span
          className="text-[13px] font-bold"
          style={{ color: danger ? COLORS.dangerInk : COLORS.ink }}
        >
          {time}
        </span>
      </div>
      <div
        className="text-[13px] font-bold"
        style={{ color: danger ? COLORS.danger : COLORS.ink }}
      >
        {title}
      </div>
      <div
        className="text-[12px] leading-[1.5]"
        style={{ color: danger ? COLORS.dangerInk : COLORS.muted }}
      >
        {body}
      </div>
    </div>
  )
}

type Props = {
  infringement: Pick<
    InfringementQueryInfringement,
    | 'id'
    | 'infringementTimestamp'
    | 'periodStartDate'
    | 'periodEndDate'
    | 'valueKind'
    | 'valueMagnitudeMinutes'
    | 'recordedTotalMinutes'
    | 'limitMinutes'
    | 'infringementCategory'
    | 'activities'
  >
  ferrySpecificConditions?: FerrySpecificCondition[]

  wrapperProps?: React.HTMLAttributes<HTMLDivElement>
}

export const InfringementDetailV1: React.FC<Props> = ({
  infringement,
  ferrySpecificConditions = [],
  wrapperProps,
}) => {
  const getActivityColor = useActivityColor()
  const t = useTranslate({
    driving: 'common.driving',
    work: 'common.work',
    rest: 'common.rest',
    pause: 'common.pause',
    weeklyRest: 'infringements.weekly-rest',
    dailyRest: 'infringements.daily-rest',
    shortBreak: 'common.pause',
    available: 'common.availability',
    unavailable: 'common.unavailable',
    ferryEvent: 'infringements.ferry-event',
    ferryEvents: 'infringements.ferry-events',
    ferryStart: 'infringements.ferry-start',
    ferryEnd: 'infringements.ferry-end',

    lowerLimit: 'infringements.lower-limit',
    recordedValue: 'infringements.recorded-value',
    detectedAt: 'infringements.detected-at',
    startedAt: 'infringements.started-at',
    overage: 'infringements.overage',
    deficit: 'infringements.deficit',
    over: 'infringements.over',
    categoryBTitle: 'infringements.category-b-title',
    categoryBWeeklyTitle: 'infringements.category-b-weekly-title',
    categoryBFortnightlyTitle: 'infringements.category-b-fortnightly-title',
    categoryCTitle: 'infringements.category-c-title',
    categoryDTitle: 'infringements.category-d-title',
    categoryDWeeklyTitle: 'infringements.category-d-weekly-title',
    dailyLimit: 'infringements.daily-limit',
    weeklyLimit: 'infringements.weekly-limit',
    fortnightlyLimit: 'infringements.fortnightly-limit',
    requiredBreak: 'infringements.required-break',
    requiredRest: 'infringements.required-rest',
    limitCrossedAt: 'infringements.limit-crossed-at',
    drivingResumedAt: 'infringements.driving-resumed-at',
    actualDriving: 'infringements.actual-driving',
    // Key is named `actual-break` for historical reasons but the
    // translation content is "Continuous driving" / "Sammenhengende
    // kjøring" — the C rule is about continuous driving before the
    // 4h30 break, so the on-screen label reads correctly even though
    // the key name suggests otherwise. Don't rename the key without
    // migrating all 7 locale files in lockstep.
    actualBreak: 'infringements.actual-break',
    actualRest: 'infringements.actual-rest',
    evaluationWindow: 'infringements.evaluation-window',
    categoryBHint: 'infringements.category-b-hint',
    categoryBWeeklyHint: 'infringements.category-b-weekly-hint',
    categoryBFortnightlyHint: 'infringements.category-b-fortnightly-hint',
    categoryCHint: 'infringements.category-c-hint',
    categoryDHint: 'infringements.category-d-hint',
    categoryDReducedHint: 'infringements.category-d-reduced-hint',
    categoryDSplitHint: 'infringements.category-d-split-hint',
    categoryDMultiManningHint: 'infringements.category-d-multi-manning-hint',
    categoryDWeeklyHint: 'infringements.category-d-weekly-hint',
    overLimit: 'infringements.over-limit',
    overWithoutBreak: 'infringements.over-without-break',
    shortBy: 'infringements.short-by',
    underLimit: 'infringements.under-limit',
    hoursAbbrev: 'common.hours-abbrev',
    minutesAbbrev: 'common.minutes-abbrev',
    category: 'common.category',

    activityTimeline: 'infringements.activity-timeline',
    keyEvents: 'infringements.key-events',

    periodStartedTitle: 'infringements.period-started-title',
    periodStartedBody: 'infringements.period-started-body',
    periodEndedTitle: 'infringements.period-ended-title',
    periodEndedBody: 'infringements.period-ended-body',
    infringementDetectedBody: [
      'infringements.infringement-detected-body',
      { value: 'value', limit: 'limit' },
    ],
    infringementDetectedGeneric: 'infringements.infringement-detected-generic',
    detectedBodyBDaily: [
      'infringements.detected-body-b-daily',
      { limit: 'limit', value: 'value', delta: 'delta' },
    ],
    detectedBodyBWeekly: [
      'infringements.detected-body-b-weekly',
      { limit: 'limit', value: 'value', delta: 'delta' },
    ],
    detectedBodyC: [
      'infringements.detected-body-c',
      { limit: 'limit', value: 'value', delta: 'delta' },
    ],
    detectedBodyDDaily: [
      'infringements.detected-body-d-daily',
      { limit: 'limit', value: 'value', delta: 'delta' },
    ],
    detectedBodyDWeekly: [
      'infringements.detected-body-d-weekly',
      { limit: 'limit', value: 'value', delta: 'delta' },
    ],
    detectedBodyDMultiManning: [
      'infringements.detected-body-d-multi-manning',
      { limit: 'limit', value: 'value', delta: 'delta' },
    ],
    tickWindowClosed: 'infringements.tick-window-closed',
    tickLimitCrossed: 'infringements.tick-limit-crossed',
    segmentSplit: [
      'infringements.segment-split',
      { before: 'before', after: 'after' },
    ],
    windowClosedDeficit: [
      'infringements.window-closed-deficit',
      { time: 'time', deficit: 'deficit' },
    ],

    valueDrivingOverage: [
      'infringements.value-driving-overage',
      { value: 'value' },
    ],
    valueBreakOverage: [
      'infringements.value-break-overage',
      { value: 'value' },
    ],
    valueRestDeficit: ['infringements.value-rest-deficit', { value: 'value' }],
  })

  const category = infringement.infringementCategory

  const infringementAt = parseISO(infringement.infringementTimestamp)
  const periodStart = infringement.periodStartDate
    ? parseISO(infringement.periodStartDate)
    : null
  const periodEnd = infringement.periodEndDate
    ? parseISO(infringement.periodEndDate)
    : null

  const rowLabels = useMemo<Record<RowKey, string>>(
    () => ({
      drive: t.driving,
      work: t.work,
      availability: t.available,
      pause: t.pause,
      rest: t.rest,
      shortBreak: t.shortBreak,
      unavailable: t.unavailable,
      ferry: t.ferryEvents,
    }),
    [
      t.available,
      t.driving,
      t.ferryEvents,
      t.pause,
      t.rest,
      t.shortBreak,
      t.unavailable,
      t.work,
    ]
  )

  const ferryTypeLabels: Record<FerrySpecificCondition['type'], string> =
    useMemo(
      () => ({
        FERRY: t.ferryEvent,
        FERRY_START: t.ferryStart,
        FERRY_END: t.ferryEnd,
      }),
      [t.ferryEnd, t.ferryEvent, t.ferryStart]
    )

  const restKindByActivityId = useMemo(
    () => buildRestKindByActivityId(infringement.activities),
    [infringement.activities]
  )

  const features = useMemo(() => {
    const byRow: Record<RowKey, GanttFeature[]> = {
      drive: [],
      work: [],
      availability: [],
      pause: [],
      rest: [],
      shortBreak: [],
      unavailable: [],
      ferry: [],
    }

    infringement.activities.forEach(a => {
      const restKind = isRestActivity(a.activityType)
        ? restKindByActivityId.get(a.id) ?? null
        : null
      const row = restKind
        ? restKind === 'shortBreak'
          ? 'shortBreak'
          : 'rest'
        : activityTypeToRow(a.activityType)
      if (!row) return
      const startAt = parseISO(a.datetimeStart)
      const endAt = parseISO(a.datetimeEnd)
      const duration = differenceInMinutes(endAt, startAt)
      const tooltipRowLabel =
        restKind === 'weeklyRest'
          ? t.weeklyRest
          : restKind === 'dailyRest'
          ? t.dailyRest
          : rowLabels[row]
      // Per-activity color via the shared salary-gantt palette so both views
      // stay visually consistent. Status takes precedence over group in
      // GanttFeatureItemCard, so bar colors follow the exact activity type.
      const color = getActivityColor({ activityType: a.activityType })
      byRow[row].push({
        id: a.id,
        name: '',
        startAt,
        endAt,
        duration,
        status: {
          id: a.activityType,
          name: tooltipRowLabel,
          color,
        },
        group: {
          id: row,
          name: rowLabels[row],
          color,
        },
        tooltipContent: (
          <div>
            <h3>{tooltipRowLabel}</h3>
            <ul>
              <li>
                {format(startAt, 'HH:mm')} – {format(endAt, 'HH:mm')}
              </li>
              <li>
                {Math.floor(duration / 60)}h {duration % 60}m
              </li>
              {a.vehicleRegistrationNumber && (
                <li>{a.vehicleRegistrationNumber}</li>
              )}
            </ul>
          </div>
        ),
      })
    })

    ferrySpecificConditions.forEach(condition => {
      const startAt = parseISO(condition.timestamp)
      const endAt = addMinutes(startAt, FERRY_EVENT_WIDTH_MINUTES)
      const label = ferryTypeLabels[condition.type]
      byRow.ferry.push({
        id: condition.id,
        name: '',
        startAt,
        endAt,
        duration: 0,
        hideDuration: true,
        status: {
          id: condition.type,
          name: label,
          color: '#0EA5E9',
        },
        group: {
          id: 'ferry',
          name: rowLabels.ferry,
          color: '#0EA5E9',
        },
        tooltipContent: (
          <div>
            <h3>{label}</h3>
            <ul>
              <li>{format(startAt, 'PP HH:mm')}</li>
            </ul>
          </div>
        ),
      })
    })

    return byRow
  }, [
    ferrySpecificConditions,
    ferryTypeLabels,
    infringement.activities,
    restKindByActivityId,
    rowLabels,
    getActivityColor,
    t.dailyRest,
    t.weeklyRest,
  ])

  const orderedRows = useMemo<RowKey[]>(() => {
    const rows: RowKey[] = [
      'drive',
      'work',
      'availability',
      'pause',
      'shortBreak',
      'rest',
      'unavailable',
      'ferry',
    ]
    return rows.filter(row => features[row].length > 0)
  }, [features])

  const rowDurationMinutes = useMemo(() => {
    const durations: Record<RowKey, number> = {
      drive: 0,
      work: 0,
      availability: 0,
      pause: 0,
      rest: 0,
      shortBreak: 0,
      unavailable: 0,
      ferry: 0,
    }

    infringement.activities.forEach(activity => {
      const restKind = isRestActivity(activity.activityType)
        ? restKindByActivityId.get(activity.id) ?? null
        : null
      const row = restKind
        ? restKind === 'shortBreak'
          ? 'shortBreak'
          : 'rest'
        : activityTypeToRow(activity.activityType)
      if (!row) return

      const startAt = parseISO(activity.datetimeStart)
      const endAt = parseISO(activity.datetimeEnd)
      const clippedStart = periodStart ? max([startAt, periodStart]) : startAt
      const clippedEnd = periodEnd ? min([endAt, periodEnd]) : endAt
      const minutes = differenceInMinutes(clippedEnd, clippedStart)
      if (minutes <= 0) return

      durations[row] += minutes
    })

    return durations
  }, [infringement.activities, periodEnd, periodStart, restKindByActivityId])

  const { datetimeStart, datetimeEnd } = useMemo(() => {
    // Visible window = span of the activities the backend actually returned
    // (which it widens via INFRINGEMENT_ACTIVITIES_EXTEND_* args, see
    // Infringement.tsx). Anchoring on real feature bounds keeps the chart in
    // sync with available data — no empty space past the last activity.
    const candidates: Date[] = [infringementAt]
    if (periodEnd) candidates.push(periodEnd)
    // For Cat D the overdraft band is projected `magnitudeMin` minutes
    // past `periodEnd` (see `bandSegments`). Keep the right edge wide
    // enough so the band isn't clipped.
    const catLetterLocal = categoryLetter(category.identifier)
    const magnitudeMinLocal = infringement.valueMagnitudeMinutes ?? 0
    if (periodEnd && catLetterLocal === 'D' && magnitudeMinLocal > 0) {
      candidates.push(addMinutes(periodEnd, magnitudeMinLocal))
    }
    orderedRows.forEach(row =>
      features[row].forEach(f => {
        candidates.push(f.startAt)
        candidates.push(f.endAt)
      })
    )
    const rawStart = subHours(min(candidates), 1)
    const rawEnd = addHours(max(candidates), 1)
    rawStart.setMinutes(0, 0, 0)
    rawEnd.setMinutes(0, 0, 0)
    return { datetimeStart: rawStart, datetimeEnd: rawEnd }
  }, [
    features,
    infringementAt,
    orderedRows,
    periodEnd,
    category.identifier,
    infringement.valueMagnitudeMinutes,
  ])

  // For Cat D (rest deficit) the "driving resumed" moment (used for the
  // stat card and story copy) is when the rest ended. Find the rest
  // activity that contains or immediately follows the raw timestamp and
  // anchor to its end. This is what the driver-facing narrative cares
  // about ("Kjøring startet kl. 08:57"). The regulatory-evaluation tick
  // on the chart uses a different anchor — see `chartTickAt` below.
  const effectiveInfringementAt = useMemo(() => {
    if (categoryLetter(category.identifier) !== 'D') return infringementAt
    const rests = infringement.activities
      .filter(a => isRestActivity(a.activityType))
      .map(a => ({
        start: parseISO(a.datetimeStart),
        end: parseISO(a.datetimeEnd),
      }))
      .sort((a, b) => a.start.getTime() - b.start.getTime())
    const containing = rests.find(
      r => r.start <= infringementAt && r.end >= infringementAt
    )
    if (containing) return containing.end
    const after = rests.find(r => r.start >= infringementAt)
    return after?.end ?? infringementAt
  }, [category.identifier, infringement.activities, infringementAt])

  // Hour/minute abbreviations are localized: Norwegian uses "t" (time)
  // for hours, not "h". All duration rendering in this view funnels
  // through here so the abbreviation choice stays consistent.
  const formatHoursMinutes = useCallback(
    (mins: number): string => {
      const abs = Math.abs(mins)
      const h = Math.floor(abs / 60)
      const m = abs % 60
      if (h && m) return `${h}${t.hoursAbbrev} ${m}${t.minutesAbbrev}`
      if (h) return `${h}${t.hoursAbbrev}`
      return `${m}${t.minutesAbbrev}`
    },
    [t.hoursAbbrev, t.minutesAbbrev]
  )
  const formatDelta = useCallback(
    (mins: number): string => {
      const sign = mins >= 0 ? '+' : '−'
      return `${sign} ${formatHoursMinutes(mins)}`
    },
    [formatHoursMinutes]
  )
  const catLetter = categoryLetter(category.identifier)
  const family = categoryFamily(category.identifier)
  const isDeficitCategory = catLetter === 'D'
  // Backend-normalized values. `reasonValue` has inconsistent semantics
  // across category families (excess for B1–B8, total for everything else),
  // so we avoid reading it directly on the client. Instead we use:
  // - `valueMagnitudeMinutes` — always-positive overage/deficit
  // - `recordedTotalMinutes` — the measured quantity (e.g. total continuous
  //   driving for C, total rest for D)
  // - `limitMinutes` — the regulatory limit (e.g. 270 = 4h30 for C break rule)
  // Backend exposes minutes as ints — source is minute-resolution tachograph
  // data, so integer minutes avoid float rounding noise on the wire. See
  // `InfringementNode._recorded_total_hours` / `_limit_hours` in
  // infringements/schema.py (those internal helpers still work in hours; the
  // graphene resolvers convert to minutes at the edge).
  const magnitudeMin = infringement.valueMagnitudeMinutes ?? 0
  // Signed delta in minutes: positive for overage, negative for deficit.
  // Drives the percentage hint and legacy `formatDelta` display.
  const deltaMin =
    magnitudeMin === 0 ? null : isDeficitCategory ? -magnitudeMin : magnitudeMin
  const recordedTotalMin = infringement.recordedTotalMinutes ?? 0
  const limitMin = infringement.limitMinutes ?? 0
  const deltaLabel =
    catLetter === 'B'
      ? t.overLimit
      : catLetter === 'C'
      ? t.overWithoutBreak
      : catLetter === 'D'
      ? t.shortBy
      : isDeficitCategory
      ? t.deficit
      : t.overage
  // Title is specialized per family: weekly rest ≠ daily rest, weekly
  // driving ≠ daily driving. Keeps the UI copy aligned with the regulatory
  // article the backend classified the infringement under.
  const categoryTitle = (() => {
    switch (family) {
      case 'B_DAILY':
        return t.categoryBTitle
      case 'B_WEEKLY':
        return t.categoryBWeeklyTitle
      case 'B_FORTNIGHTLY':
        return t.categoryBFortnightlyTitle
      case 'C_BREAK':
        return t.categoryCTitle
      case 'D_WEEKLY':
        return t.categoryDWeeklyTitle
      case 'D_ORDINARY':
      case 'D_REDUCED':
      case 'D_SPLIT':
      case 'D_MULTI_MANNING':
        return t.categoryDTitle
      default:
        return category.description
    }
  })()
  // Keep the limit label aligned with `categoryTitle` / `limitHint`: B9–B16
  // are weekly/fortnightly ceilings, not daily, so showing "Daily limit"
  // next to "Weekly driving time exceeded" contradicts the rule text.
  const limitLabel = (() => {
    switch (family) {
      case 'B_DAILY':
        return t.dailyLimit
      case 'B_WEEKLY':
        return t.weeklyLimit
      case 'B_FORTNIGHTLY':
        return t.fortnightlyLimit
      case 'C_BREAK':
        return t.requiredBreak
      case 'D_ORDINARY':
      case 'D_REDUCED':
      case 'D_SPLIT':
      case 'D_MULTI_MANNING':
      case 'D_WEEKLY':
        return t.requiredRest
      default:
        return t.lowerLimit
    }
  })()
  const startedAtLabel = isDeficitCategory
    ? t.drivingResumedAt
    : t.limitCrossedAt
  const recordedLabel =
    catLetter === 'B'
      ? t.actualDriving
      : catLetter === 'C'
      ? t.actualBreak
      : catLetter === 'D'
      ? t.actualRest
      : t.recordedValue
  // Hint text on the "required rest / max driving" stat card. For D this
  // is crucial — the regulatory minimum depends on whether we're looking at
  // ordinary, reduced, split or weekly rest, and labelling all of them
  // "standard daily minimum" misrepresents D4–D9 and D13–D18.
  const limitHint = (() => {
    switch (family) {
      case 'B_DAILY':
        return t.categoryBHint
      case 'B_WEEKLY':
        return t.categoryBWeeklyHint
      case 'B_FORTNIGHTLY':
        return t.categoryBFortnightlyHint
      case 'C_BREAK':
        return t.categoryCHint
      case 'D_ORDINARY':
        return t.categoryDHint
      case 'D_REDUCED':
        return t.categoryDReducedHint
      case 'D_SPLIT':
        return t.categoryDSplitHint
      case 'D_MULTI_MANNING':
        return t.categoryDMultiManningHint
      case 'D_WEEKLY':
        return t.categoryDWeeklyHint
      default:
        return undefined
    }
  })()
  // Regulatory limit the card compares against. Sourced from the backend's
  // `limitMinutes` so C shows the 4h30 break rule rather than the C1/C2/C3
  // severity threshold.
  const limitValue = limitMin > 0 ? formatHoursMinutes(limitMin) : null
  const recordedTotal =
    recordedTotalMin > 0 ? formatHoursMinutes(recordedTotalMin) : null
  // Backend returns a structured (kind, magnitude) pair so localization and
  // unit formatting stay on the client. See `InfringementNode.value_kind` /
  // `value_magnitude_minutes` in infringements/schema.py.
  const valueSummary = useMemo<string | null>(() => {
    if (magnitudeMin <= 0) return null
    const value = formatHoursMinutes(magnitudeMin)
    switch (infringement.valueKind) {
      case 'DRIVING_OVERAGE':
        return t.valueDrivingOverage({ value })
      case 'BREAK_OVERAGE':
        return t.valueBreakOverage({ value })
      case 'REST_DEFICIT':
        return t.valueRestDeficit({ value })
      default:
        return null
    }
  }, [formatHoursMinutes, infringement.valueKind, magnitudeMin, t])

  const overlayRow = overlayRowForCategory(category.identifier)
  const [ganttZoom, setGanttZoom] = useState(100)
  const ganttPxPerMinute = (GANTT_COLUMN_WIDTH_PX * ganttZoom) / 100 / (60 * 24)
  const ganttWrapRef = useRef<HTMLDivElement>(null)

  const toTimelineLeft = (d: Date) =>
    differenceInMinutes(d, datetimeStart) * ganttPxPerMinute

  // Bar segments for the overlay row:
  // - Cat D (rest deficit): single "overdraft" band projected *after*
  //   window close, extending `magnitudeMin` minutes past `periodEnd`.
  //   It's a magnitude visualization rather than a temporal claim about
  //   where rest should have been (that would clash with recorded rest
  //   filling the pre-deadline slot). The gap from the window-close tick
  //   to the band's right edge equals the deficit.
  // - Cat B/C (driving / break): the last `magnitudeMin` minutes of real
  //   drive-time within the evaluation window, emitted as one segment per
  //   drive activity touched. Walking in drive-time (not clock-time)
  //   keeps the painted length equal to the stat-card magnitude even
  //   when short rests sit inside the overage window.
  type BandSegment = { left: number; width: number; startAt: Date; endAt: Date }
  const bandSegments = useMemo<BandSegment[]>(() => {
    if (isDeficitCategory) {
      if (!periodEnd || deltaMin === null) return []
      const deficitMin = Math.abs(deltaMin)
      if (deficitMin <= 0) return []
      // Project the shortfall as an "overdraft" bar starting at window
      // close and extending `deficitMin` minutes past it. This is a
      // *magnitude* visualization rather than a temporal claim — the rest
      // was not required to happen after the deadline, but visually this
      // shows "you were X minutes short of the 24h deadline" in a way
      // that's impossible to miss. Painting inside the window instead
      // tended to be fully covered by recorded rest (correctly, since
      // most of the rest *was* inside the window), leaving the band
      // invisible for small deficits even though the deficit itself was
      // the whole point. The bar's right edge is where the rest *should*
      // have reached; the gap between the window-close tick and the
      // bar's right edge equals the deficit.
      const segStart = periodEnd
      const segEnd = addMinutes(periodEnd, deficitMin)
      return [
        {
          left: toTimelineLeft(segStart),
          width: deficitMin * ganttPxPerMinute,
          startAt: segStart,
          endAt: segEnd,
        },
      ]
    }
    // Cat B/C: walk drive activities end-first, take `magnitudeMin` min
    // of drive-time, emit one segment per activity touched. Clip to the
    // evaluation window [periodStart, periodEnd] before walking — drive
    // outside this window belongs to other infringements and must not
    // paint red here.
    if (magnitudeMin <= 0) return []
    const driveActs = infringement.activities
      .filter(a => activityTypeToRow(a.activityType) === overlayRow)
      .map(a => {
        let startAt = parseISO(a.datetimeStart)
        let endAt = parseISO(a.datetimeEnd)
        if (periodStart && startAt < periodStart) startAt = periodStart
        if (periodEnd && endAt > periodEnd) endAt = periodEnd
        return { startAt, endAt }
      })
      .filter(a => a.endAt > a.startAt)
      .filter(a => !periodEnd || a.startAt < periodEnd)
      .filter(a => !periodStart || a.endAt > periodStart)
      .sort((a, b) => b.endAt.getTime() - a.endAt.getTime())
    let remainingMin = magnitudeMin
    const segments: BandSegment[] = []
    for (const act of driveActs) {
      if (remainingMin <= 0) break
      const actMin = differenceInMinutes(act.endAt, act.startAt)
      if (actMin <= 0) continue
      const take = Math.min(actMin, remainingMin)
      const segStart = addMinutes(act.endAt, -take)
      const segEnd = act.endAt
      segments.push({
        left: toTimelineLeft(segStart),
        width: take * ganttPxPerMinute,
        startAt: segStart,
        endAt: segEnd,
      })
      remainingMin -= take
    }
    // Earlier activities come out last (reverse-walk); re-sort for
    // chronological tooltip reading.
    return segments
      .filter(b => b.width > 0)
      .sort((a, b) => a.startAt.getTime() - b.startAt.getTime())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDeficitCategory,
    periodStart,
    periodEnd,
    deltaMin,
    magnitudeMin,
    infringement.activities,
    overlayRow,
    ganttPxPerMinute,
  ])

  // Anchor for the red tick on the timeline.
  // - Cat D (rest deficit): the close of the 24h evaluation window — that's
  //   when the rule evaluates. `effectiveInfringementAt` often points at
  //   "driving resumed" which can land *outside* the window (e.g. window
  //   closes 08:24, driving resumes 08:57) and would contradict the
  //   semantics.
  // - Cat B/C (driving / break): the start of the first band segment —
  //   i.e. the moment the regulatory limit was crossed. Everything after
  //   that is excess. The backend's `infringementTimestamp` is sometimes
  //   the severity-trigger (e.g. C3's 6h threshold, not the 4h30 rule),
  //   which diverges from the band's left edge and confuses the viewer
  //   ("why is the line at 15:00 when the excess starts at 13:30?").
  const chartTickAt = useMemo(() => {
    const letter = categoryLetter(category.identifier)
    if (letter === 'D' && periodEnd) return periodEnd
    if ((letter === 'B' || letter === 'C') && bandSegments.length > 0)
      return bandSegments[0]!.startAt
    return effectiveInfringementAt
  }, [category.identifier, bandSegments, effectiveInfringementAt, periodEnd])
  const tickLeft = toTimelineLeft(chartTickAt)

  // Drive-row tooltips for B/C can read misleadingly: a drive activity
  // that runs 09:00–17:00 (8h) visually appears to stop at the red band's
  // left edge (e.g. 13:30) because the band overlay sits on top of the
  // bar. Hovering the bar then shows "8h 0m" while only ~4h30 of navy is
  // actually visible. For the overlay row, we wrap the tooltip with a
  // "limit crossed at HH:mm" line and a pre/post-limit split, so the
  // number matches what the eye sees.
  const enrichedFeatures = useMemo(() => {
    if (isDeficitCategory || bandSegments.length === 0) return features
    const limitCrossedAt = bandSegments[0]!.startAt
    const next: Record<RowKey, GanttFeature[]> = { ...features }
    next[overlayRow] = features[overlayRow].map(f => {
      if (f.endAt <= limitCrossedAt || f.startAt >= limitCrossedAt) return f
      const beforeMin = differenceInMinutes(limitCrossedAt, f.startAt)
      const afterMin = differenceInMinutes(f.endAt, limitCrossedAt)
      const act = infringement.activities.find(a => a.id === f.id)
      return {
        ...f,
        tooltipContent: (
          <div>
            <h3>{rowLabels[overlayRow]}</h3>
            <ul>
              <li>
                {format(f.startAt, 'HH:mm')} – {format(f.endAt, 'HH:mm')} (
                {formatHoursMinutes(beforeMin + afterMin)})
              </li>
              <li>
                {t.limitCrossedAt}: {format(limitCrossedAt, 'HH:mm')}
              </li>
              <li>
                {t.segmentSplit({
                  before: formatHoursMinutes(beforeMin),
                  after: formatHoursMinutes(afterMin),
                })}
              </li>
              {act?.vehicleRegistrationNumber && (
                <li>{act.vehicleRegistrationNumber}</li>
              )}
            </ul>
          </div>
        ),
      }
    })
    return next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    features,
    bandSegments,
    isDeficitCategory,
    overlayRow,
    rowLabels,
    infringement.activities,
    t,
  ])

  // Story card body is specialized per category family: it states the
  // rule in one line and then the measured outcome, so a leader unfamiliar
  // with 561/2006 can read the card without also knowing the regulation.
  // Falls back to the generic "recorded X against limit Y" copy when we
  // don't have both numbers (rare — means backend omitted them).
  const detectedBody = useMemo<string>(() => {
    if (!(recordedTotal && limitValue) || magnitudeMin <= 0) {
      return recordedTotal && limitValue
        ? t.infringementDetectedBody({
            value: recordedTotal,
            limit: limitValue,
          })
        : t.infringementDetectedGeneric
    }
    const delta = formatHoursMinutes(magnitudeMin)
    const params = { limit: limitValue, value: recordedTotal, delta }
    switch (family) {
      case 'B_DAILY':
        return t.detectedBodyBDaily(params)
      case 'B_WEEKLY':
      case 'B_FORTNIGHTLY':
        return t.detectedBodyBWeekly(params)
      case 'C_BREAK':
        return t.detectedBodyC(params)
      case 'D_ORDINARY':
      case 'D_REDUCED':
      case 'D_SPLIT':
        return t.detectedBodyDDaily(params)
      case 'D_MULTI_MANNING':
        return t.detectedBodyDMultiManning(params)
      case 'D_WEEKLY':
        return t.detectedBodyDWeekly(params)
      default:
        return t.infringementDetectedBody({
          value: recordedTotal,
          limit: limitValue,
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family, recordedTotal, limitValue, magnitudeMin, t])

  // For B/C the stat card shows "Grense passert kl." (limit crossed) and
  // should mirror the tick at the band's left edge. For D the label is
  // "Kjøring startet kl." (driving resumed) — a different concept from
  // window close, so keep using `effectiveInfringementAt` (rest end).
  const detectedSource = isDeficitCategory
    ? effectiveInfringementAt
    : chartTickAt
  const detectedAt = format(detectedSource, 'HH:mm')
  const detectedDate = format(detectedSource, 'PP')

  // Key-events cards show only HH:mm. For rules with multi-day evaluation
  // windows (e.g. daily rest: periodStart and periodEnd are 24h apart)
  // the same hour string can appear on both start and end cards, making
  // them indistinguishable. If any of the three event moments fall on a
  // different calendar date from the others, prepend a compact date.
  // Uses `detectedSource` — the same timestamp the middle card renders,
  // so the diff-check reflects what's actually on screen.
  const eventDatesDiffer = useMemo(() => {
    const ds: Date[] = []
    if (periodStart) ds.push(periodStart)
    ds.push(detectedSource)
    if (periodEnd) ds.push(periodEnd)
    return ds.some(d => !isSameDay(d, ds[0]!))
  }, [periodStart, detectedSource, periodEnd])
  const formatEventTime = (d: Date) =>
    eventDatesDiffer ? format(d, 'd. MMM HH:mm') : format(d, 'HH:mm')

  useEffect(() => {
    const wrap = ganttWrapRef.current
    if (!wrap) return
    const ganttEl = wrap.querySelector<HTMLDivElement>('.gantt')
    if (!ganttEl) return
    const frame = requestAnimationFrame(() => {
      const sidebar = ganttEl.querySelector<HTMLElement>(
        '[data-roadmap-ui="gantt-sidebar"]'
      )
      const sidebarWidth = sidebar?.getBoundingClientRect().width ?? 0
      const tickPx =
        differenceInMinutes(chartTickAt, datetimeStart) * ganttPxPerMinute
      const target = Math.max(
        0,
        tickPx + sidebarWidth - ganttEl.clientWidth / 2
      )
      ganttEl.scrollLeft = target
    })
    return () => cancelAnimationFrame(frame)
  }, [datetimeStart, chartTickAt, ganttPxPerMinute])

  // Sub-hour tick overlay density based on zoom. NOTE: This is a local
  // workaround — the shared <GanttHeader> in src/components/kibo-ui/gantt
  // hard-codes 1-hour ticks. Long-term, make the step zoom-aware in
  // DailyHeader so all Gantt-consuming views get denser ticks uniformly.
  const minorStepMin =
    ganttZoom >= 400 ? 5 : ganttZoom >= 200 ? 15 : ganttZoom >= 100 ? 30 : 0
  const minorTicks = useMemo(() => {
    if (minorStepMin === 0) return []
    const ticks: { at: Date; left: number }[] = []
    let current = startOfHour(datetimeStart)
    if (current < datetimeStart) current = addHours(current, 1)
    while (current <= datetimeEnd) {
      for (let m = minorStepMin; m < 60; m += minorStepMin) {
        const at = addMinutes(current, m)
        if (at > datetimeEnd) break
        ticks.push({ at, left: toTimelineLeft(at) })
      }
      current = addHours(current, 1)
    }
    return ticks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datetimeStart, datetimeEnd, minorStepMin, ganttPxPerMinute])

  return (
    <div
      className="flex flex-col gap-6 rounded-[20px] py-6"
      {...wrapperProps}
      style={{ background: '#F4F6F9', ...wrapperProps?.style }}
    >
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:flex md:gap-3.5">
        {limitValue && (
          <StatCard label={limitLabel} value={limitValue} hint={limitHint} />
        )}
        {recordedTotal && (
          <StatCard label={recordedLabel} value={recordedTotal} />
        )}
        {(valueSummary || deltaMin !== null) && (
          <StatCard
            label={deltaLabel}
            value={valueSummary || formatDelta(deltaMin ?? 0)}
            tone="danger"
            hint={(() => {
              if (deltaMin === null || limitMin <= 0) return undefined
              const rawPct = Math.round((Math.abs(deltaMin) / limitMin) * 100)
              // Cap presentation at 999%. Extreme fortnightly overages
              // can easily produce 2000%+ which reads as noise and blows
              // out the hint column. Over 999%, show ">999%" so the
              // reader still gets the "absurdly large" signal without
              // the specific number dominating the card.
              const pctLabel = rawPct > 999 ? '>999' : `${rawPct}`
              // "X% for lite" (literally "X% too little") reads awkwardly in
              // Norwegian. For deficits use "X% under kravet" ("under the
              // requirement") which is the standard phrasing. Overages keep
              // "X% over".
              return `${pctLabel}% ${isDeficitCategory ? t.underLimit : t.over}`
            })()}
          />
        )}
        <StatCard
          label={startedAtLabel}
          value={detectedAt}
          hint={detectedDate}
        />
      </div>

      {/* Gantt */}
      <div
        className="flex flex-col gap-3.5 rounded-xl bg-white p-4 md:p-6"
        style={{ border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[14px] font-bold"
              style={{ color: COLORS.ink }}
            >
              {t.activityTimeline}
            </span>
            <span className="text-[11px]" style={{ color: COLORS.faint }}>
              {format(datetimeStart, 'PP HH:mm')} →{' '}
              {format(datetimeEnd, 'PP HH:mm')}
            </span>
          </div>
          <div
            className="flex items-center gap-1 rounded-lg"
            style={{ border: `1px solid ${COLORS.border}` }}
          >
            <button
              className="flex h-8 w-8 items-center justify-center disabled:opacity-40"
              disabled={ganttZoom <= ZOOM_MIN}
              onClick={() => setGanttZoom(z => prevZoom(z))}
              style={{ color: COLORS.muted }}
              type="button"
            >
              <Minus size={14} />
            </button>
            <span
              className="min-w-[36px] text-center text-[11px] font-semibold"
              style={{ color: COLORS.ink }}
            >
              {ganttZoom}%
            </span>
            <button
              className="flex h-8 w-8 items-center justify-center disabled:opacity-40"
              disabled={ganttZoom >= ZOOM_MAX}
              onClick={() => setGanttZoom(z => nextZoom(z))}
              style={{ color: COLORS.muted }}
              type="button"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div ref={ganttWrapRef} className="relative w-full max-w-full pb-4">
          <GanttProvider
            className="border pb-5"
            range="daily"
            zoom={ganttZoom}
            datetimeStart={datetimeStart}
            datetimeEnd={datetimeEnd}
            cursorFollowTooltips
          >
            <GanttSidebar
              className="!w-28 sm:!w-56 md:!w-64"
              compact={isMobile}
            >
              {orderedRows.map(row => {
                const rowFeatures = features[row]
                return (
                  <GanttSidebarItem
                    feature={{
                      id: row,
                      name: rowLabels[row],
                      startAt: rowFeatures[0]?.startAt ?? datetimeStart,
                      endAt:
                        rowFeatures[rowFeatures.length - 1]?.endAt ??
                        datetimeEnd,
                      duration: rowDurationMinutes[row],
                      hideDuration: row === 'ferry',
                    }}
                    key={row}
                  />
                )
              })}
            </GanttSidebar>
            <GanttTimeline>
              <GanttHeader />
              {minorTicks.length > 0 && (
                <div
                  className="pointer-events-none absolute left-0 z-10"
                  style={{
                    top: 'calc(var(--gantt-header-height) - 18px)',
                    height: 14,
                  }}
                >
                  {minorTicks.map(({ at, left }) => (
                    <div
                      className="absolute -translate-x-1/2 text-[10px]"
                      key={at.getTime()}
                      style={{ left, color: COLORS.faint }}
                    >
                      {format(at, 'HH:mm')}
                    </div>
                  ))}
                </div>
              )}
              {periodStart && periodEnd && (
                <>
                  <div
                    className="absolute inset-y-0 z-0"
                    style={{
                      left: toTimelineLeft(periodStart),
                      width:
                        toTimelineLeft(periodEnd) - toTimelineLeft(periodStart),
                    }}
                  >
                    <Card
                      className="h-full w-full border-0 p-0"
                      cursorFollow
                      style={{
                        background: 'rgba(15, 31, 58, 0.04)',
                        borderLeft: `1px dashed ${COLORS.faint}`,
                        borderRight: `1px dashed ${COLORS.faint}`,
                      }}
                      tooltipContent={
                        <div className="flex flex-col gap-0.5 text-left">
                          <div className="font-semibold">
                            {t.evaluationWindow}
                          </div>
                          {isSameDay(periodStart, periodEnd) ? (
                            <>
                              <div className="opacity-80">
                                {format(periodStart, 'PP')}
                              </div>
                              <div className="whitespace-nowrap">
                                {format(periodStart, 'HH:mm')} →{' '}
                                {format(periodEnd, 'HH:mm')}
                              </div>
                            </>
                          ) : (
                            <>
                              <div>{format(periodStart, 'PP HH:mm')}</div>
                              <div>→ {format(periodEnd, 'PP HH:mm')}</div>
                            </>
                          )}
                          <div className="mt-1 opacity-80">
                            {formatDelta(
                              differenceInMinutes(periodEnd, periodStart)
                            ).replace('+ ', '')}
                          </div>
                        </div>
                      }
                    />
                  </div>
                </>
              )}
              <GanttFeatureList>
                {orderedRows.map(row => {
                  const segments = row === overlayRow ? bandSegments : []
                  return (
                    <div className="relative" key={row}>
                      <GanttFeatureRow
                        className="min-w-full"
                        features={enrichedFeatures[row]}
                        handleViewFeature={() => void 0}
                      />
                      {segments.map((seg, i) => (
                        <div
                          className="pointer-events-auto absolute top-0.5"
                          key={`${seg.startAt.getTime()}-${i}`}
                          style={{
                            left: Math.round(seg.left),
                            width: Math.round(seg.width),
                            height: 'calc(var(--gantt-row-height) - 4px)',
                          }}
                        >
                          <Card
                            className="h-full w-full border-[1.5px] p-0"
                            cursorFollow
                            style={{
                              background: COLORS.dangerBg,
                              borderColor: COLORS.danger,
                            }}
                            tooltipContent={
                              <div className="flex flex-col gap-1 text-left">
                                <div className="font-semibold">
                                  {categoryTitle}
                                </div>
                                {isDeficitCategory && periodEnd ? (
                                  <div>
                                    {t.windowClosedDeficit({
                                      time: format(periodEnd, 'HH:mm'),
                                      deficit: formatHoursMinutes(magnitudeMin),
                                    })}
                                  </div>
                                ) : (
                                  <>
                                    <div>
                                      {format(seg.startAt, 'HH:mm')} –{' '}
                                      {format(seg.endAt, 'HH:mm')}
                                    </div>
                                    {valueSummary && <div>{valueSummary}</div>}
                                  </>
                                )}
                                {recordedTotal && limitValue && (
                                  <div className="opacity-80">
                                    {recordedLabel}: {recordedTotal} /{' '}
                                    {limitLabel} {limitValue}
                                  </div>
                                )}
                              </div>
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )
                })}
              </GanttFeatureList>
              <div
                className="absolute z-20"
                style={{
                  top: 'var(--gantt-header-height)',
                  bottom: 0,
                  left: tickLeft - 5,
                  width: 12,
                }}
              >
                <Card
                  className="h-full w-full border-0 bg-transparent p-0"
                  cursorFollow
                  tooltipContent={
                    <div className="flex flex-col gap-1 text-left">
                      <div className="font-semibold">{categoryTitle}</div>
                      <div className="opacity-80">
                        {isDeficitCategory
                          ? t.tickWindowClosed
                          : t.tickLimitCrossed}
                      </div>
                      <div>{format(chartTickAt, 'PP HH:mm')}</div>
                    </div>
                  }
                >
                  <div
                    className="pointer-events-none absolute inset-y-0"
                    style={{
                      left: 5,
                      width: 2,
                      background: COLORS.danger,
                      borderRadius: 1,
                    }}
                  />
                </Card>
              </div>
            </GanttTimeline>
          </GanttProvider>
        </div>
      </div>

      {/* Story */}
      {(periodStart || periodEnd) && (
        <div className="flex flex-col gap-3">
          <span className="text-[14px] font-bold" style={{ color: COLORS.ink }}>
            {t.keyEvents}
          </span>
          <div className="flex flex-col gap-3 md:flex-row md:gap-3.5">
            {periodStart && (
              <StoryCard
                step={1}
                time={formatEventTime(periodStart)}
                title={t.periodStartedTitle}
                body={t.periodStartedBody}
              />
            )}
            <StoryCard
              step={periodStart ? 2 : 1}
              time={formatEventTime(detectedSource)}
              title={categoryTitle}
              body={detectedBody}
              tone="danger"
            />
            {periodEnd && (
              <StoryCard
                step={periodStart ? 3 : 2}
                time={formatEventTime(periodEnd)}
                title={t.periodEndedTitle}
                body={t.periodEndedBody}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
